import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PageHeader } from '../design';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import { RefreshCw, ThumbsUp, ThumbsDown, Eye, MessageSquareWarning, BookOpen, Camera, Loader2, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';

const pctTone = (pct) => {
  if (pct == null) return 'bg-slate-500/15 text-slate-400 border-slate-500/30';
  if (pct >= 75) return 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30';
  if (pct >= 50) return 'bg-amber-500/15 text-amber-500 border-amber-500/30';
  return 'bg-red-500/15 text-red-500 border-red-500/30';
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function HelpInsights() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await governanceAPI.getHelpInsights();
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load help insights', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ---- Docs screenshots refresh ----
  const [shotStatus, setShotStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const pollRef = useRef(null);

  const loadShotStatus = useCallback(async () => {
    try {
      const res = await governanceAPI.getHelpScreenshotStatus();
      setShotStatus(res.data);
      return res.data;
    } catch (_e) { return null; }
  }, []);

  useEffect(() => {
    loadShotStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadShotStatus]);

  const refreshShots = async () => {
    setRefreshing(true);
    try {
      await governanceAPI.refreshHelpScreenshots();
      toast.success('Refreshing screenshots…', { description: 'Capturing the current UI — this takes ~30–60s.' });
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        const s = await loadShotStatus();
        if (s && !s.running) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRefreshing(false);
          const st = s.last_run?.status;
          if (st === 'success') toast.success('Screenshots refreshed');
          else if (st === 'partial') toast.warning('Some screens failed to capture');
          else if (st === 'failed') toast.error('Screenshot capture failed', { description: s.last_run?.error });
        }
      }, 4000);
    } catch (e) {
      setRefreshing(false);
      toast.error('Could not start capture', { description: e?.response?.data?.detail || e.message });
    }
  };

  const isRunning = refreshing || shotStatus?.running;

  const guides = data?.guides || [];
  const totals = data?.totals || {};
  const comments = data?.comments || [];
  const maxViews = Math.max(1, ...guides.map((g) => g.views || 0));

  return (
    <div className="space-y-6" data-testid="help-insights-page">
      <PageHeader
        eyebrow="Docs Ops"
        title="Help Insights"
        subtitle="Platform-wide Help Center usage across every tenant — most-opened guides, helpful ratios, and what users say is missing. Use it to see where docs need work."
        actions={
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="help-insights-refresh">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        }
      />

      {/* Totals */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          { label: 'Guide opens', value: totals.views ?? '—', testid: 'stat-views' },
          { label: 'Marked helpful', value: totals.helpful_up ?? '—', testid: 'stat-up' },
          { label: 'Marked not helpful', value: totals.helpful_down ?? '—', testid: 'stat-down' },
          { label: 'Feedback notes', value: comments.length, testid: 'stat-comments' },
        ].map((s) => (
          <Card key={s.label} className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)]">
            <CardContent className="p-4">
              <div className="gov-text-tertiary text-xs uppercase tracking-wide">{s.label}</div>
              <div className="gov-text-primary mt-1 text-2xl font-semibold" data-testid={s.testid}>{s.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Docs screenshots refresh */}
      <Card className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)]" data-testid="help-screenshots-card">
        <CardHeader>
          <CardTitle className="gov-text-primary flex items-center gap-2 text-lg">
            <Camera className="h-4 w-4" /> Docs screenshots
          </CardTitle>
          <CardDescription className="gov-text-secondary">
            One-click re-capture of the six Help Center product screens from the live app, so the guides always show the current UI.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="gov-text-tertiary text-xs">
              {shotStatus?.last_run?.finished_at
                ? <>Last refreshed <span className="gov-text-secondary">{timeAgo(shotStatus.last_run.finished_at)}</span> · status <span className="gov-text-secondary">{shotStatus.last_run.status}</span></>
                : 'Not refreshed yet — using the bundled screenshots.'}
            </div>
            <Button size="sm" onClick={refreshShots} disabled={isRunning} data-testid="help-screenshots-refresh">
              {isRunning
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Capturing…</>
                : <><Camera className="mr-2 h-4 w-4" /> Refresh screenshots</>}
            </Button>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {(shotStatus?.screens || []).map((s) => (
              <div key={s.name} className="rounded-lg border border-[var(--gov-border)] bg-[var(--gov-bg-input)] p-2.5 text-center" data-testid={`help-shot-${s.name}`}>
                <div className="gov-text-primary flex items-center justify-center gap-1 text-xs font-medium capitalize">
                  {s.exists && <CheckCircle2 className="h-3 w-3 text-emerald-500" />}{s.name}
                </div>
                <div className="gov-text-tertiary mt-0.5 text-[10px]">{s.updated_at ? timeAgo(s.updated_at) : 'missing'}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Most-opened guides */}
      <Card className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)]">
        <CardHeader>
          <CardTitle className="gov-text-primary flex items-center gap-2 text-lg">
            <BookOpen className="h-4 w-4" /> Most-opened guides
          </CardTitle>
          <CardDescription className="gov-text-secondary">
            {loading ? 'Loading…' : `${guides.length} guide(s) with recorded activity, most-opened first`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : guides.length === 0 ? (
            <div className="gov-text-tertiary py-10 text-center text-sm" data-testid="help-insights-empty">
              No guide activity recorded yet. Views and ratings appear here as users open the Help Center.
            </div>
          ) : (
            <div className="space-y-3">
              {guides.map((g) => (
                <div
                  key={g.guide_id}
                  className="rounded-lg border border-[var(--gov-border)] bg-[var(--gov-bg-input)] p-4"
                  data-testid={`help-insight-row-${g.guide_id}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="gov-text-primary font-medium">{g.title}</div>
                      <code className="gov-text-tertiary text-[10px]">{g.guide_id}</code>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="gov-text-secondary flex items-center gap-1" title="Opens">
                        <Eye className="h-3.5 w-3.5" /> {g.views}
                      </span>
                      <span className="flex items-center gap-1 text-emerald-500" title="Helpful">
                        <ThumbsUp className="h-3.5 w-3.5" /> {g.helpful_up}
                      </span>
                      <span className="flex items-center gap-1 text-red-500" title="Not helpful">
                        <ThumbsDown className="h-3.5 w-3.5" /> {g.helpful_down}
                      </span>
                      <Badge variant="outline" className={pctTone(g.helpful_pct)}>
                        {g.helpful_pct == null ? 'No ratings' : `${g.helpful_pct}% helpful`}
                      </Badge>
                    </div>
                  </div>
                  {/* Views bar */}
                  <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[var(--gov-bg-surface)]">
                    <div className="h-full rounded-full bg-[var(--gov-accent)]" style={{ width: `${(g.views / maxViews) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Feedback comments */}
      <Card className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)]">
        <CardHeader>
          <CardTitle className="gov-text-primary flex items-center gap-2 text-lg">
            <MessageSquareWarning className="h-4 w-4" /> “What was missing?” notes
          </CardTitle>
          <CardDescription className="gov-text-secondary">
            Free-text notes left with a 👎 — the clearest signal of where a guide needs fixing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : comments.length === 0 ? (
            <div className="gov-text-tertiary py-10 text-center text-sm" data-testid="help-insights-comments-empty">
              No feedback notes yet. When a user marks a guide unhelpful and adds a note, it shows up here.
            </div>
          ) : (
            <div className="space-y-2.5">
              {comments.map((c, i) => (
                <div key={i} className="rounded-lg border border-[var(--gov-border)] bg-[var(--gov-bg-input)] p-3" data-testid={`help-comment-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="outline" className="text-[10px]">{c.title}</Badge>
                    <span className="gov-text-tertiary text-[10px]">{timeAgo(c.created_at)}</span>
                  </div>
                  <p className="gov-text-secondary mt-1.5 text-sm">“{c.comment}”</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
